import { inject, Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, map, Observable, of, shareReplay, throwError, timeout } from 'rxjs';
import { LoginResponse, Token, User } from '../models/logininterface';
import { LoginService } from '../service/login.service';
import { PermissionService } from './menu.permission.service';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { LoaderService } from '../../shared/components/loader/loader.service';
import { ToasterService } from '../../shared/components/toaster/toaster.service';
import { ConfirmDialogService } from '../../shared/components/dialog/confirm-dialog.service';
import { ConfirmDialogComponent } from '../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { IdleTimeOutService } from '../service/idle-time-out.service';
import { MatDialog } from '@angular/material/dialog';
import { SignalRService } from '../service/signalr.service';
import { AgentBackgroundService } from '../service/Agentbackground.service';
import { AgentType } from '../models/agent-type.enum';
import { AGENT_CONFIG } from '../models/agent.config';
import { AUTH_SESSION_STORAGE_KEY, getAuthSessionId, normalizeAuthSessionId } from './auth-session';


export type UserTypeRole = 'agent' | 'crm' | null;
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  locationId:any
  locationName:any
  private logoutTimer: any;
  private tokenRefreshTimer: any;
  private idleLogoutTimer: any; 
  isIdleLogout = false; 
  private isLoggedOut = false;
  private isSessionWarningOpen = false;
  private refreshInProgress$: Observable<{ token: string; csrfToken: string }> | null = null;
  private activeDialogRef: any = null; 


  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  private tokenSubject = new BehaviorSubject<Token | null>(null);
  token$ = this.tokenSubject.asObservable();

  isAuthenticated$ = this.token$.pipe(map((token) => !!token?.accessToken));
 private userType: UserTypeRole = null;
  private readonly translate = inject(TranslateService);
  private readonly TAB_AUTH_KEY = 'tab-authenticated';
  constructor(private loginService: LoginService, 
    private injector: Injector,
    private router: Router,
    private menuPermissionService:PermissionService,
    private loader:LoaderService,
    private toaster:ToasterService,
    private confirmDialog: ConfirmDialogService,
    private idleService: IdleTimeOutService,
    @Inject(PLATFORM_ID) private platformId: object,
    private dialog: MatDialog,
    private signalRService: SignalRService,
    private backgroundService:AgentBackgroundService,

  ) {
 
    if (isPlatformBrowser(this.platformId)) {
      const storedUserType = sessionStorage.getItem('userType') as 'agent' | 'crm' | null;
      if (storedUserType) {
        this.userType = storedUserType;
      }
    }
    this.loadStoredAuthData();
    

    // Each tab owns its session; other tabs must not reload or clear this user.
    

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
     
      
      if (this.isAuthPage()) {
      
        
       
        if (this.activeDialogRef) {
          this.activeDialogRef.close();
          this.activeDialogRef = null;
        }
        
 
        this.dialog.closeAll();
        
     
        if (this.tokenRefreshTimer) {
          clearTimeout(this.tokenRefreshTimer);
          this.tokenRefreshTimer = null;
        }
        if (this.logoutTimer) {
          clearTimeout(this.logoutTimer);
          this.logoutTimer = null;
        }
        if (this.idleLogoutTimer) {
          clearTimeout(this.idleLogoutTimer);
          this.idleLogoutTimer = null;
        }
        

        this.idleService?.stopWatching?.();
      } else {
        
        
        const token = this.getToken();
        const expiryDate = this.tokenSubject.value?.expiryDate || '';
        
        if (token && expiryDate && !this.isLoggedOut) {
         
          this.idleService?.startWatching?.();
          
    
          this.scheduleTokenRefresh(expiryDate, token);
        }
      }
    });
  }

  private setTabAuthenticated(): void {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem(this.TAB_AUTH_KEY, 'true');
     
    }
  }

  isTabAuthenticated(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const authenticated = sessionStorage.getItem(this.TAB_AUTH_KEY) === 'true';
    return authenticated;
  }

   ngOnInit() {
    this.loader.disableLoader();
  }

  ngOnDestroy() {
    this.loader.enableLoader();
  }
 setUserType(type: 'agent' | 'crm') {
    this.userType = type;

    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('userType', type);
    }
  }

  getUserType(): 'agent' | 'crm' | null {
    if (this.userType) {
      return this.userType;
    }
    const storedType = isPlatformBrowser(this.platformId)
      ? sessionStorage.getItem('userType') as 'agent' | 'crm' | null : null;
    if (storedType) {
      this.userType = storedType;
      return storedType;
    }

    if (isPlatformBrowser(this.platformId)) {
      const storedLocalType = sessionStorage.getItem('userType') as 'agent' | 'crm' | null;
      if (storedLocalType === 'agent' || storedLocalType === 'crm') {
        this.userType = storedLocalType;
        return storedLocalType;
      }

      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          const derivedType = Number(user?.isAgent) === 1 ? 'agent' : 'crm';
          this.userType = derivedType;
          sessionStorage.setItem('userType', derivedType);
          return derivedType;
        } catch {
          return null;
        }
      }
    }

    this.userType = null;
    return null;
  }

  private checkExistingLogin(newUserType: 'agent' | 'crm'): Observable<boolean> {
  return new Observable(observer => {
    const existingUserType = this.getUserType();
    const existingToken = this.getToken();

   
      if (!existingToken || !existingUserType) {
      
        observer.next(true);
        observer.complete();
        return;
      }

    /* * Same user type still show the popup. */
      const existingTypeLabel = existingUserType === 'crm' ? 'Admin' : 'Agent';
      const newTypeLabel = newUserType === 'crm' ? 'Admin' : 'Agent';

      const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        disableClose: true,
        data: {
          title: this.translate.instant('MULTIPLE_LOGIN.TITLE'),
          message: this.translate.instant('MULTIPLE_LOGIN.MESSAGE', { existingTypeLabel, newTypeLabel }),
          confirmText: this.translate.instant('MULTIPLE_LOGIN.CONFIRM'),
          cancelText: this.translate.instant('MULTIPLE_LOGIN.CANCEL'),
          icon: 'warning'
        }
      });

      confirmDialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        observer.next(false);
        observer.complete();
        return;
      }
        observer.next(true);
        observer.complete();
      });
    });
  }

private loadStoredAuthData(): void {
  if (!isPlatformBrowser(this.platformId) || !this.getSessionId()) {
    return;
  }

  const storedUser = sessionStorage.getItem('user');
  this.loadUserData(storedUser, this.getToken() ?? '');
}
private loadUserData(
  storedUser: string | null,
  accessToken: string
): void {
  if (!storedUser) {
    return;
  }

  try {
    const parsedUser: User = JSON.parse(storedUser);
    this.userSubject.next(parsedUser);

    this.setUserPermissions(parsedUser);
    this.restartSignalRConnection(parsedUser, accessToken);
  } catch (error) {
  
    sessionStorage.removeItem('user');
  }
}

private setUserPermissions(parsedUser: User): void {

  const allPermissions =
    (parsedUser.rolePermissions as any[])
      ?.flatMap(role => role.permissions || []) || [];

  const agentConfig = AGENT_CONFIG[this.getAgentType()];

  const isNovibetUser =
    agentConfig?.User === 'NovibetUser';

  this.menuPermissionService.setPermissions(
    allPermissions,
    isNovibetUser
  );
}

private restartSignalRConnection(
  parsedUser: User,
  accessToken: string
): void {
  if (!parsedUser.agentId || !parsedUser.locationId || !accessToken) {
    return;
  }

  const agentId = String(parsedUser.agentId).toLowerCase();
  const locationId = String(parsedUser.locationId).toLowerCase();
  if (agentId === 'null' || agentId === 'undefined' || locationId === 'null' || locationId === 'undefined') {
    return;
  }

  const connectionId = `${agentId}_${locationId}`;
  const sessionId = this.getSessionId();
  if (sessionId) {
    this.signalRService.startConnection(connectionId,
      () => this.getSessionId() === sessionId ? this.getToken() ?? '' : '', sessionId);
  }
}

private loadTokenData(
  accessToken: string,
  csrfToken: string,
  expiryDate: string
): void {
  if (!accessToken) {
    return;
  }

  this.tokenSubject.next({
    accessToken,
    csrfToken,
    expiryDate: expiryDate || ''
  });

  if (!expiryDate) {
    return;
  }

  this.scheduleTokenRefresh(expiryDate, accessToken);
  this.idleService.startWatching();
}
  login(payload: any): Observable<LoginResponse> {
    return this.checkExistingLogin('crm').pipe(
      switchMap(canProceed => {
        if (!canProceed) throw new Error('Login cancelled by user');
        return this.loginService.login(payload);
      }),
      tap(response => {
        this.storeAuthData(response);
        this.setTabAuthenticated();
        this.setUserType('crm');
      })
    );
  }

 agentLogin(payload: any): Observable<LoginResponse> {
  return this.checkExistingLogin('agent').pipe(
    switchMap((canProceed) => {
      if (!canProceed) {
        // Instead of observer.error, throw an error in the RxJS stream
        throw new Error('Login cancelled by user');
      }

      return this.loginService.agentLogin(payload);
    }),
    switchMap((response) => {
      this.storeAuthData(response);
      this.setTabAuthenticated();
      this.setUserType('agent');
      // 🔄 Crucial Step: switchMap forces the stream to WAIT for the setup API
      // It passes the response forward once the setup is finished saving to sessionStorage
      return this.getAgentSetupandActivations(response).pipe(
        map(() => response) // Return the original login response
      );
    })
  );
}
getAgentSetupandActivations(response: any): Observable<any> {
  return this.backgroundService.getAgentById(response.agentId).pipe(
    tap((agentData) => {
      if (this.getSessionId() !== normalizeAuthSessionId(response.sessionId)) {
        throw new Error('The login session changed before agent setup completed.');
      }
      // Safely save to sessionStorage when the data arrives
      sessionStorage.setItem('activatedServices', JSON.stringify(agentData.activatedServices));
    })
  );
}

  getlocationId(id: any,name:string) {
    this.locationId = id;
    this.locationName=name;
    
 }


updateUserLocationId(locationId: any,locationName:string) {
    this.locationId = locationId;
    this.locationName=locationName
    const existingUser = sessionStorage.getItem('user');
    if (existingUser) {
        const user = JSON.parse(existingUser);
        user.locationId = locationId;
        user.locationName=locationName;
        sessionStorage.setItem('user', JSON.stringify(user));
        this.userSubject.next(user); 

        const token = this.getToken();
        if (user.agentId && locationId && token) {
          this.restartSignalRConnection(user, token);
        }
    }
}
updateDeviceFlags(deviceFlags: {
  scanner: boolean;
  printer: boolean;
  softOTP: boolean;
  hardOTP: boolean;
  signaturePad: boolean;
  camera: boolean;
  uploading: boolean;
  nbgpos: boolean;
  cardLinkPOS: boolean;
}) {
  const existingUser = sessionStorage.getItem('user');
  if (existingUser) {
    const user = JSON.parse(existingUser);
    
 
    user.deviceFlags = deviceFlags;
    

    
    sessionStorage.setItem('user', JSON.stringify(user));
    this.userSubject.next(user);
  }
}
private storeAuthData(response: LoginResponse) {
  const sessionId = normalizeAuthSessionId(response.sessionId);
  if (!sessionId || this.getTokenSessionId(response.accessToken) !== sessionId ||
      this.getTokenUserId(response.accessToken) !== String(response.id)) {
    throw new Error('The server returned invalid session credentials. Please sign in again.');
  }
  const utcExpiry = new Date(response.expiryDate).toISOString();
  this.isLoggedOut = false;

  // A new login in this tab must not inherit the previous user's cached forms.
  // Keep browser location and flags collected by the login page before auth.
  const loginKeys = new Set(['agentLocation']);
  Object.keys(sessionStorage).forEach(key => {
    if (!loginKeys.has(key)) {
      sessionStorage.removeItem(key);
    }
  });
  sessionStorage.setItem(AUTH_SESSION_STORAGE_KEY, sessionId);
  this.locationId = null;
  this.locationName = null;

  try {
    const { accessToken, csrfToken, expiryDate } = response;

    if (!this.locationId && response.location) {
      if (Array.isArray(response.location) && response.location.length === 1) {
        this.locationId = response.location[0].id;
        this.locationName = response.location[0].name;
      } else if (!Array.isArray(response.location) && (response.location as any)?.id) {
        this.locationId = (response.location as any).id;
        this.locationName = (response.location as any).name;
      }
    }

    const user: User = {
      id: response.id,
      userName: response.userName,
      role: response.role,
      roleIds: response.roleIds ?? [],
      queueNameIds: response.queueNameIds ?? [],
      companyId: response.companyId,
      email: response.email,
      rolePermissions: response.rolePermissions ?? [],
      location: response.location,
      locationId: this.locationId ?? null,
      locationName: this.locationName ?? null,
      agentName: response.agentName,
      agentId: response.agentId,
      agentNumber: response.agentNumber,
      financialSuspended: response.financialSuspended,
      AgentRole: response.tellerRole,
      RfCode: response.rfCode,
      isAgent: response.isAgent
    };

    // Save user first because getAgentType() reads from sessionStorage
    sessionStorage.setItem('user', JSON.stringify(user));

    // Get all backend permissions
    const allPermissions =
      (response.rolePermissions as any[])
        ?.flatMap(role => role.permissions ?? []) || [];

    // Get current agent configuration
    const agentConfig = this.getAgentConfiguration();

    // Check whether the logged-in user is Novibet
    const isNovibetUser =
      this.getAgentType() === AgentType.NOVIBET;

    // Set permissions
    this.menuPermissionService.setPermissions(
      allPermissions,
      isNovibetUser
    );

    this.userSubject.next(user);

    this.tokenSubject.next({
      accessToken,
      csrfToken,
      expiryDate
    });

    this.scheduleTokenRefresh(
      utcExpiry,
      accessToken
    );

    this.idleService.startWatching();

    this.restartSignalRConnection(user, accessToken);

  } catch (error) {
    throw error;
  }
}


  getToken(): string | null {
    return this.tokenSubject.value?.accessToken ?? null;
  }

  getSessionId(): string | null {
    return getAuthSessionId();
  }

  getCsrfToken(): string | null {
    return this.tokenSubject.value?.csrfToken ?? null;
  }

  getRefreshToken(): string | null {
    return null;
  }

  getUserName(): string | null {
    const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.userName;
    }
    return null;
  }

  getUserEmail(): string | null {
    const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.email;
    }
    return null;
  }

   getUserId(): string | null {
    const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.id;
    }
    return null;
  }
  getLocationName():string|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.locationName;
    }
    return null;
  }
   getLocationNaID():string|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.locationId;
    }
    return null;
  }
   getfinancialSuspended():number|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.financialSuspended;
    }
    return null;
  }
getAgentName():string|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.agentName;
    }
    return null;
  }
  getAgentNumber(): string | null {
  const userString = sessionStorage.getItem('user');
  if (userString) {
    const user = JSON.parse(userString);
    return user.agentNumber;
  }
  return null;
}
getAgentId():string|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.agentId;
    }
    return null;
  }
  getAgentRole():string|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.AgentRole;
    }
    return null;
  }
  getRfCode():string|null{
     const userString = sessionStorage.getItem('user');
    if (userString) {
      const user = JSON.parse(userString);
      return user.RfCode;
    }
    return null;
  }

private scheduleAutoLogout(expiryDate: string) {
  if (!expiryDate) return;

  const expiryTime = new Date(expiryDate).getTime();
  const currentTime = new Date().getTime();
  const timeUntilExpiry = expiryTime - currentTime;

  if (timeUntilExpiry <= 0) {
    this.handleSessionExpired();
    return;
  }

  this.logoutTimer = setTimeout(() => {
    this.handleSessionExpired();
  }, timeUntilExpiry);
}

private handleSessionExpired() {
 
  if (this.isLoggedOut || this.isAuthPage()) {
    return;
  }
  this.toaster.showWarning('Your session has expired. Please log in again.', 'Warning');
  this.logout();
}

logout(revokeServerSession: boolean = true) {
  if (this.isLoggedOut) return;
  const accessToken = this.getToken();
  const csrfToken = this.getCsrfToken();
  const sessionId = this.getSessionId();
  const userType = this.getUserType();
  const shouldRevokeServerSession = revokeServerSession && !!accessToken && !!sessionId;

  this.isIdleLogout = false;
  this.isLoggedOut = true;
  this.isSessionWarningOpen = false;
  this.refreshInProgress$ = null;

  this.loader.enableLoader();
  this.loader.show();

  sessionStorage.removeItem(this.TAB_AUTH_KEY);


  if (this.logoutTimer) {
    clearTimeout(this.logoutTimer);
    this.logoutTimer = null;
  }
  if (this.tokenRefreshTimer) {
    clearTimeout(this.tokenRefreshTimer);
    this.tokenRefreshTimer = null;
  }
  if (this.idleLogoutTimer) {
    clearTimeout(this.idleLogoutTimer);
    this.idleLogoutTimer = null;
  }


  if (this.activeDialogRef) {
    this.activeDialogRef.close();
    this.activeDialogRef = null;
  }


  this.idleService?.stopWatching?.();
  

  this.signalRService.stopConnection();


  


  

  
 
  this.userType = null;
  
  
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('userType');
  sessionStorage.removeItem('countryList');
  sessionStorage.removeItem('roleList');

  this.userSubject.next(null);
  this.tokenSubject.next(null);
  this.menuPermissionService.setPermissions([]);
 const keysToKeep = new Set<string>(['auth-tab-id']);

Object.keys(sessionStorage).forEach((key) => {
  if (!keysToKeep.has(key)) {
    sessionStorage.removeItem(key);
  }
});

  if (shouldRevokeServerSession) {
    this.loginService.logout(accessToken!, csrfToken ?? '', sessionId!).pipe(
      timeout(2500),
      finalize(() => {
        if (!this.getSessionId()) this.navigateToLogin(userType);
      })
    ).subscribe({error: () => {}});
  } else {
    this.navigateToLogin(userType);
  }
}

private navigateToLogin(userType: UserTypeRole): void {
if (userType === 'crm') {
  window.location.replace('/login');
} else if (userType === 'agent') {
  window.location.replace('/agentlogin');
} else if (this.router.url.startsWith('/crm')) {
  // This runs if userType is null/undefined but the user is on a CRM page
  window.location.replace('/login');
} else {
  // Fallback default for everything else
  window.location.replace('/agentlogin');
}
}


  public showIdleWarning() {
    if (this.isLoggedOut || this.isAuthPage()) return;
    const token = this.getToken();
    if (token && this.getSessionId()) {
      this.showSessionWarning(null, token, true);
    } else {
      this.logout();
    }
  }
  // Returns logged-in agent type
getAgentType(): AgentType {
  const userString = sessionStorage.getItem('user');

  if (!userString) {
    return AgentType.NORMAL;
  }

  const user = JSON.parse(userString);

  switch (user.isAgent) {
    case 2:
      return AgentType.NOVIBET;
    default:
      return AgentType.NORMAL;
  }
}

  // Returns complete agent configuration
  getAgentConfiguration() {
    return AGENT_CONFIG[this.getAgentType()];
  }

  // Returns allowed sidebar menus
  getAllowedMenus(): string[] {
    return this.getAgentConfiguration().sidebar;
  }

  // Returns landing page
  getDashboardPage(): string {
    return this.getAgentConfiguration().landingPage;
  }

  // Returns location selection flag
  // requiresLocationSelection(): boolean {
  //   return this.getAgentConfiguration().requiresLocationSelection;
  // }

  private scheduleTokenRefresh(expiryDate: string, accessToken: string) {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  private isAuthPage(): boolean {
    const excludedRoutes = [
      '/login',
      '/agentlogin',
      '/resetpassword',
      '/forgot-password',
      '/changepassword',
      '/agentchangepassword',
      '/novibet/transaction-details',
      '/novibet/transaction-withdrawal-details',
      '/service-location-reports',
      '/404'
    ];

    const currentUrl = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '');
    return excludedRoutes.some(route => currentUrl.includes(route));
  }

  private showSessionWarning(_expiryDate: string | null, accessToken: string, _isIdleWarning: boolean) {
    if (this.isSessionWarningOpen || this.isLoggedOut || this.isAuthPage()) return;
    const sessionId = this.getSessionId();
    if (!sessionId) return;
    this.isSessionWarningOpen = true;
    const deadline = Date.now() + 5 * 60000;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      maxWidth: '450px',
      minWidth: '450px',
      panelClass: 'session-warning-dialog',
      disableClose: true,
      data: {
        title: this.translate.instant('SESSION_EXPIRING.TITLE'),
        message: this.translate.instant('SESSION_EXPIRING.MESSAGE'),
        confirmText: this.translate.instant('SESSION_EXPIRING.CONFIRM'),
        cancelText: this.translate.instant('SESSION_EXPIRING.CANCEL'),
        icon: 'warning'
      }
    });
    this.activeDialogRef = dialogRef;
    let settled = false;
    const finish = (continueSession: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // A late callback must never clear a newer login's warning or timers.
      if (this.getSessionId() !== sessionId || this.activeDialogRef !== dialogRef) return;
      this.idleLogoutTimer = null;
      this.activeDialogRef = null;
      this.isSessionWarningOpen = false;
      dialogRef.close();
      if (this.isLoggedOut || this.isAuthPage()) return;
      if (continueSession && Date.now() < deadline) {
        this.isIdleLogout = false;
        this.refreshToken(accessToken);
      } else {
        this.logout();
      }
    };
    const timer = setTimeout(() => finish(false), 5 * 60000);
    this.idleLogoutTimer = timer;
    dialogRef.afterClosed().subscribe((confirmed: any) => finish(confirmed === true));
  }

  public refreshSession(): Observable<{ token: string; csrfToken: string }> {
    if (this.isLoggedOut) {
      return throwError(() => new Error('User is logged out'));
    }

    if (this.refreshInProgress$) {
      return this.refreshInProgress$;
    }

    const expectedUserId = this.getUserId();
    const expectedSessionId = this.getSessionId();
    if (!expectedUserId || !expectedSessionId) {
      return throwError(() => new Error('This tab has no session. Please sign in again.'));
    }

    const refreshRequest$ = this.loginService.RefreshToken({}).pipe(
      map((response: any) => {
        if (!response) {
          throw new Error('Empty refresh response');
        }

        const token = response.token || response.Token;
        const csrfToken = response.csrfToken || response.CsrfToken || '';

        if (!token) {
          throw new Error('Access token was not returned from refresh');
        }

        // Bind every response to the session that requested it, including when
        // the same user has multiple sessions or switches accounts mid-request.
        const refreshedUserId = this.getTokenUserId(token);
        const refreshedSessionId = this.getTokenSessionId(token);
        const responseSessionId = response.sessionId || response.SessionId;
        if (this.isLoggedOut || this.getSessionId() !== expectedSessionId ||
            this.getUserId() !== expectedUserId || refreshedUserId !== expectedUserId ||
            refreshedSessionId !== expectedSessionId ||
            (responseSessionId && normalizeAuthSessionId(responseSessionId) !== expectedSessionId)) {
          throw new Error('The session changed before refresh completed. Please sign in again.');
        }

        let expiryDate = response.expiryDate || response.ExpiryDate;
        if (!expiryDate) {
          expiryDate = this.getExpiryFromJwt(token) || new Date(Date.now() + 15 * 60000).toISOString();
        }

        if (!this.isLoggedOut) {
          this.updateTokenData({ token, csrfToken, expiryDate });
          this.setTabAuthenticated();
        }

        return { token, csrfToken };
      }),
      finalize(() => {
        if (this.refreshInProgress$ === refreshRequest$) this.refreshInProgress$ = null;
      }),
      // Route guards can unsubscribe while HTTP is still running. Only source
      // completion may release the shared request, otherwise a second refresh
      // reuses the old cookie and triggers server replay protection.
      shareReplay(1)
    );
    this.refreshInProgress$ = refreshRequest$;
    return this.refreshInProgress$;
  }

  public ensureAuthenticated(): Observable<boolean> {
    if (!this.getSessionId()) return of(false);
    if (this.getToken()) {
      return of(true);
    }

    if (!isPlatformBrowser(this.platformId) || this.isAuthPage()) {
      return of(false);
    }

    return this.refreshSession().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private getExpiryFromJwt(token: string): string | null {
    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
        const payload = JSON.parse(payloadJson);
        if (payload?.exp) {
          return new Date(payload.exp * 1000).toISOString();
        }
      }
    } catch {
      // Ignore parse failure
    }
    return null;
  }

  private getTokenUserId(token: string): string | null {
    try {
      const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
      return typeof payload.sub === 'string' ? payload.sub : null;
    } catch {
      return null;
    }
  }

  private getTokenSessionId(token: string): string | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const encoded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')));
      return normalizeAuthSessionId(payload.SessionId);
    } catch {
      return null;
    }
  }

private refreshToken(_currentToken: string) {
  const requestedSessionId = this.getSessionId();
  this.refreshSession().subscribe({
    next: (response) => {
      if (this.getSessionId() !== requestedSessionId) return;
     
      this.toaster.showSuccess('Session extended successfully!');
      

      this.isIdleLogout = false;
    },
    error: (err) => {
      if (this.getSessionId() !== requestedSessionId) return;
    
      this.toaster.showError('Failed to refresh session. Please login again.');
      this.logout();
    }
  });
}

private updateTokenData(response: any) {
  if (this.isLoggedOut) {
    return;
  }

  const accessToken = response.token || response.Token;
  const csrfToken = response.csrfToken || response.CsrfToken || '';
  let expiryDate = response.expiryDate || response.ExpiryDate;

  if (!expiryDate && accessToken) {
    expiryDate = this.getExpiryFromJwt(accessToken) || '';
  }

  if (!accessToken) {
    return;
  }

  this.tokenSubject.next({ accessToken, csrfToken, expiryDate });
  const user = this.userSubject.value;
  if (user) this.restartSignalRConnection(user, accessToken);
  

  this.scheduleTokenRefresh(expiryDate, accessToken);
  

  this.idleService.startWatching();
  
  
}
  public triggerSessionWarning() {
    if (this.isAuthPage()) {
      return;
    }
    const token = this.getToken();
    if (token) {
      const expiryDate = this.tokenSubject.value?.expiryDate || new Date().toISOString();
      this.showSessionWarning(expiryDate, token,false);
    } else {
      this.logout();
    }
  }
  
}
